import { useCollection } from '@hb/react'
import {
  adminConsentFormsCollection,
  CheckboxField,
  Collection,
  ConsentFormTemplate,
  FieldMap,
  FieldTypes,
  getPracticeConsentFormsCollection,
  PracticeConsentForm,
} from '@hb/shared'
import { httpsCallable } from 'firebase/functions'
import { useCallback, useMemo, useState } from 'react'
import { getCollectionRef } from '../../../../backend/db/utils/collections'
import { functions } from '../../../../backend/functions/functions'
import { CollectionListEdit } from '../../../forms'
import { DefaultModal } from '../../../Modals'
import { ConsentFormTemplateModal } from '../../TemplateEditModal'
import { ConsentFormPreview } from '../ConsentFormPreview'
import { ConsentFormSettingsModal } from '../ConsentFormSettings'
import { AddConsentForm } from './AddConsentForm'

const isActiveField: CheckboxField = {
  type: FieldTypes.CHECKBOX,
  inputType: 'switch',
  yesText: 'Active',
  noText: 'Inactive',
  placeholder: 'Is Active',
}

const consentFormField: FieldMap = {
  name: 'Consent Form',
  children: {
    name: {
      type: FieldTypes.TEXT,
      placeholder: 'Consent Form Name',
    },
    isActive: isActiveField,
  },
}

const NewConsentFormModal = <T extends PracticeConsentForm | ConsentFormTemplate>({
  isOpen,
  onClose,
  collection,
  practiceId,
}: {
  isOpen: boolean
  onClose: () => void
  collection: Collection<T>
  practiceId: string | null
}) => (
  <DefaultModal
    overlayHeader
    isOpen={isOpen}
    onClose={onClose}
    render={() => (
      <AddConsentForm practiceId={practiceId} collection={collection} onClose={onClose} />
    )}
  />
)

const deleteConsentFormTemplate = httpsCallable(functions, 'deleteConsentFormTemplate')
export const ConsentFormsView = <T extends PracticeConsentForm | ConsentFormTemplate>({
  onClose,
  collection,
  practiceId,
}: {
  onClose?: () => void
  collection: Collection<T>
  practiceId: string | null
}) => {
  const [selectedConsentFormId, setSelectedConsentFormId] = useState<string | null>(null)
  const [selectedAutoDisplayId, setSelectedAutoDisplayId] = useState<string | null>(null)
  const [creatingNew, setCreatingNew] = useState(false)
  const ref = useMemo(() => getCollectionRef(collection), [collection])
  const collectionState = useCollection(collection)
  const { items: consentForms = [] } = collectionState ?? {}

  const handleDelete = useCallback(
    async (id: string) => {
      await deleteConsentFormTemplate({ templateId: id, practiceId })
    },
    [practiceId],
  )

  return (
    <>
      <CollectionListEdit<T>
        ItemPreview={({ value }) => (
          <ConsentFormPreview
            onOpenTemplate={() => setSelectedConsentFormId(value.id)}
            onOpenAutoDisplay={() => setSelectedAutoDisplayId(value.id)}
            consentForm={value}
            practiceId={practiceId}
            key={value.id}
          />
        )}
        collectionRef={ref}
        onNewItemClick={() => setCreatingNew(true)}
        itemField={consentFormField}
        getItemTitle={item => item.name}
        noItemEdit
        onDeleteItem={handleDelete}
        itemName="Consent Form"
        listName={practiceId ? 'Practice Consent Forms' : 'HB Consent Forms'}
        value={consentForms}
        onClose={onClose}
      />
      {selectedConsentFormId ? (
        <ConsentFormTemplateModal
          templatePracticeId={practiceId}
          templateId={selectedConsentFormId}
          isOpen
          onClose={() => setSelectedConsentFormId(null)}
        />
      ) : null}
      {creatingNew ? (
        <NewConsentFormModal
          isOpen={creatingNew}
          collection={collection}
          onClose={() => setCreatingNew(false)}
          practiceId={practiceId}
        />
      ) : null}
      {selectedAutoDisplayId ? (
        <ConsentFormSettingsModal
          consentFormId={selectedAutoDisplayId}
          practiceId={practiceId}
          onClose={() => setSelectedAutoDisplayId(null)}
        />
      ) : null}
    </>
  )
}

export const PracticeConsentFormsView = ({
  practiceId,
  onClose,
}: {
  practiceId: string
  onClose?: () => void
}) => {
  const collection = useMemo(() => getPracticeConsentFormsCollection(practiceId), [practiceId])
  return <ConsentFormsView collection={collection} practiceId={practiceId} onClose={onClose} />
}

export const AdminConsentFormsView = ({ onClose }: { onClose?: () => void }) => (
  <ConsentFormsView collection={adminConsentFormsCollection} practiceId={null} onClose={onClose} />
)

export const PracticeConsentFormsModal = ({
  practiceId,
  onClose,
}: {
  practiceId: string
  onClose: () => void
}) => (
  <DefaultModal
    isOpen
    size="3xl"
    overlayHeader
    closeDisabled
    onClose={onClose}
    render={() => <PracticeConsentFormsView onClose={onClose} practiceId={practiceId} />}
  />
)

export const AdminConsentFormsModal = ({ onClose }: { onClose: () => void }) => (
  <DefaultModal
    isOpen
    size="3xl"
    overlayHeader
    closeDisabled
    onClose={onClose}
    render={() => <AdminConsentFormsView onClose={onClose} />}
  />
)
